import Modal from '@/components/ui/Modal';
import { ScimSyncLogDto } from '@/services/scim.service';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  Loader2,
  TrendingUp,
  User,
  Users,
  XCircle,
  Zap,
  Shield,
  Plus,
  Edit3,
  GitMerge,
  Ban,
  ChevronDown,
  ChevronRight,
  Search
} from 'lucide-react';
import { useState } from 'react';

interface ScimSyncDetailModalProps {
  log: ScimSyncLogDto | null;
  onClose: () => void;
}

export default function ScimSyncDetailModal({ log, onClose }: ScimSyncDetailModalProps) {
  const [activeDetailTab, setActiveDetailTab] = useState<'users' | 'groups' | 'roles' | 'mappings'>('users');
  const [expandedSections, setExpandedSections] = useState<{
    created: boolean;
    updated: boolean;
    merged: boolean;
    skipped: boolean;
  }>({
    created: true,
    updated: true,
    merged: true,
    skipped: false,
  });

  // Search state for each resource type
  const [searchQueries, setSearchQueries] = useState<{
    users: string;
    groups: string;
    roles: string;
    mappings: string;
  }>({
    users: '',
    groups: '',
    roles: '',
    mappings: '',
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!log) return null;

  // Organize detailed changes by resource type
  const organizeChangesByType = () => {
    if (!log.detailed_changes) return null;

    const byType = {
      users: { created: [], updated: [], merged: [], skipped: [] } as any,
      groups: { created: [], updated: [], merged: [], skipped: [] } as any,
      roles: { created: [], updated: [], merged: [], skipped: [] } as any,
      mappings: { created: [], updated: [], merged: [], skipped: [] } as any,
    };

    // Organize created items
    log.detailed_changes.created_items?.forEach(item => {
      if (item.resource_type === 'user') byType.users.created.push(item);
      else if (item.resource_type === 'group') byType.groups.created.push(item);
      else if (item.resource_type === 'role') byType.roles.created.push(item);
      else if (item.resource_type === 'group_role_mapping') byType.mappings.created.push(item);
    });

    // Organize updated items
    log.detailed_changes.updated_items?.forEach(item => {
      if (item.resource_type === 'user') byType.users.updated.push(item);
      else if (item.resource_type === 'group') byType.groups.updated.push(item);
      else if (item.resource_type === 'role') byType.roles.updated.push(item);
      else if (item.resource_type === 'group_role_mapping') byType.mappings.updated.push(item);
    });

    // Organize merged items
    log.detailed_changes.merged_items?.forEach(item => {
      if (item.resource_type === 'user') byType.users.merged.push(item);
      else if (item.resource_type === 'group') byType.groups.merged.push(item);
      else if (item.resource_type === 'role') byType.roles.merged.push(item);
      else if (item.resource_type === 'group_role_mapping') byType.mappings.merged.push(item);
    });

    // Organize skipped items
    log.detailed_changes.skipped_items?.forEach(item => {
      if (item.resource_type === 'user') byType.users.skipped.push(item);
      else if (item.resource_type === 'group') byType.groups.skipped.push(item);
      else if (item.resource_type === 'role') byType.roles.skipped.push(item);
      else if (item.resource_type === 'group_role_mapping') byType.mappings.skipped.push(item);
    });

    return byType;
  };

  const changesByType = organizeChangesByType();

  // Filter function for search
  const filterItems = (items: any[], searchQuery: string) => {
    if (!searchQuery.trim()) return items;

    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      // Search in resource_name
      if (item.resource_name && item.resource_name.toLowerCase().includes(query)) {
        return true;
      }
      // Search in resource_id
      if (item.resource_id && item.resource_id.toLowerCase().includes(query)) {
        return true;
      }
      // Search in reason (for skipped items)
      if (item.reason && item.reason.toLowerCase().includes(query)) {
        return true;
      }
      // Search in changed_fields
      if (item.changed_fields && item.changed_fields.some((field: string) => field.toLowerCase().includes(query))) {
        return true;
      }
      // Search in merged_fields
      if (item.merged_fields && item.merged_fields.some((field: string) => field.toLowerCase().includes(query))) {
        return true;
      }
      // Search in data (stringified)
      if (item.data && JSON.stringify(item.data).toLowerCase().includes(query)) {
        return true;
      }
      return false;
    });
  };

  // Get filtered items for active tab
  const getFilteredItems = () => {
    if (!changesByType) return null;

    const currentQuery = searchQueries[activeDetailTab];
    const currentItems = changesByType[activeDetailTab];

    return {
      created: filterItems(currentItems.created, currentQuery),
      updated: filterItems(currentItems.updated, currentQuery),
      merged: filterItems(currentItems.merged, currentQuery),
      skipped: filterItems(currentItems.skipped, currentQuery),
    };
  };

  const filteredItems = getFilteredItems();

  const getSyncStatusBadge = (status: string) => {
    switch (status.toUpperCase()) {
      case 'COMPLETED':
      case 'SUCCESS':
        return (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Completed Successfully</span>
          </div>
        );
      case 'FAILED':
      case 'ERROR':
        return (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg">
            <XCircle className="w-5 h-5" />
            <span className="font-semibold">Failed</span>
          </div>
        );
      case 'IN_PROGRESS':
        return (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-semibold">In Progress</span>
          </div>
        );
      case 'PARTIAL':
        return (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Partially Completed</span>
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg">
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">{status}</span>
          </div>
        );
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(0);
    return `${minutes}m ${seconds}s`;
  };

  const calculateTotalResources = () => {
    const users = log.resources_synced?.users || {};
    const groups = log.resources_synced?.groups || {};
    const roles = log.resources_synced?.roles || {};

    return {
      total: (users.total || 0) + (groups.total || 0) + (roles.total || 0),
      created: (users.created || 0) + (groups.created || 0) + (roles.created || 0),
      updated: (users.updated || 0) + (groups.updated || 0) + (roles.updated || 0),
      deleted: (users.deleted || 0) + (groups.deleted || 0) + (roles.deleted || 0),
      skipped: (users.skipped || 0) + (groups.skipped || 0) + (roles.skipped || 0),
      failed: (users.failed || 0) + (groups.failed || 0) + (roles.failed || 0),
    };
  };

  const totals = calculateTotalResources();
  const hasRoles = (log.resources_synced?.roles?.total || 0) > 0;

  return (
    <Modal
      isOpen={!!log}
      onClose={onClose}
      title="Synchronization Details"
      subtitle={`Sync ID: ${log.sync_id}`}
      size="full"
      maxHeight="90vh"
    >
      <div className="space-y-6">
        {/* Header Section with Status */}
        <div className="bg-gradient-to-r from-brand-indigo-50 to-brand-purple-50 rounded-xl p-6 border border-brand-indigo-100">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{log.provider_name}</h3>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-600">Sync Type:</span>
                <span className="px-3 py-1 bg-white rounded-lg text-sm font-semibold text-brand-indigo-600 border border-brand-indigo-200">
                  {log.sync_type}
                </span>
                {log.is_dry_run && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold border border-blue-200">
                    Dry Run
                  </span>
                )}
              </div>
            </div>
            {getSyncStatusBadge(log.sync_status)}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-4 h-4 text-brand-indigo-600" />
                <span className="text-xs font-medium text-gray-600">Total Resources</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{totals.total}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-gray-600">Processed</span>
              </div>
              <p className="text-2xl font-bold text-green-600">
                {totals.created + totals.updated + totals.deleted}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-gray-600">Duration</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {log.performance?.total_duration_ms
                  ? formatDuration(log.performance.total_duration_ms)
                  : '-'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-yellow-600" />
                <span className="text-xs font-medium text-gray-600">Speed</span>
              </div>
              <p className="text-2xl font-bold text-yellow-600">
                {log.performance?.items_per_second
                  ? `${log.performance.items_per_second.toFixed(1)}/s`
                  : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Timeline Information */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            Timeline
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Started At</p>
              <p className="font-semibold text-gray-900">
                {new Date(log.started_at).toLocaleString()}
              </p>
            </div>
            {log.completed_at && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Completed At</p>
                <p className="font-semibold text-gray-900">
                  {new Date(log.completed_at).toLocaleString()}
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 mb-1">Triggered By</p>
              <p className="font-semibold text-gray-900">{log.triggered_by_name || log.triggered_by || 'System'}</p>
              {log.triggered_by_name && log.triggered_by && (
                <p className="text-xs text-gray-500 mt-0.5">ID: {log.triggered_by}</p>
              )}
            </div>
          </div>
        </div>

        {/* Resource Details */}
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-gray-600" />
            Resource Breakdown
          </h4>
          <div className={`grid grid-cols-1 ${hasRoles ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {/* Users */}
            <div className="bg-white rounded-xl p-6 border-2 border-blue-200 hover:border-blue-300 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">Users</h5>
                    <p className="text-sm text-gray-500">Individual accounts</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-blue-600">
                  {log.resources_synced?.users?.total || 0}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="font-semibold text-green-600">
                    +{log.resources_synced?.users?.created || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Updated</span>
                  <span className="font-semibold text-blue-600">
                    {log.resources_synced?.users?.updated || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Deleted</span>
                  <span className="font-semibold text-orange-600">
                    -{log.resources_synced?.users?.deleted || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Skipped</span>
                  <span className="font-semibold text-gray-500">
                    {log.resources_synced?.users?.skipped || 0}
                  </span>
                </div>
                {(log.resources_synced?.users?.failed || 0) > 0 && (
                  <div className="flex items-center justify-between py-2 bg-red-50 px-3 rounded-lg">
                    <span className="text-sm font-medium text-red-600">Failed</span>
                    <span className="font-bold text-red-600">
                      {log.resources_synced.users.failed}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Groups */}
            <div className="bg-white rounded-xl p-6 border-2 border-purple-200 hover:border-purple-300 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900">Groups</h5>
                    <p className="text-sm text-gray-500">User collections</p>
                  </div>
                </div>
                <div className="text-3xl font-bold text-purple-600">
                  {log.resources_synced?.groups?.total || 0}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="font-semibold text-green-600">
                    +{log.resources_synced?.groups?.created || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Updated</span>
                  <span className="font-semibold text-blue-600">
                    {log.resources_synced?.groups?.updated || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Deleted</span>
                  <span className="font-semibold text-orange-600">
                    -{log.resources_synced?.groups?.deleted || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-600">Skipped</span>
                  <span className="font-semibold text-gray-500">
                    {log.resources_synced?.groups?.skipped || 0}
                  </span>
                </div>
                {(log.resources_synced?.groups?.failed || 0) > 0 && (
                  <div className="flex items-center justify-between py-2 bg-red-50 px-3 rounded-lg">
                    <span className="text-sm font-medium text-red-600">Failed</span>
                    <span className="font-bold text-red-600">
                      {log.resources_synced.groups.failed}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Roles (if available) */}
            {hasRoles && (
              <div className="bg-white rounded-xl p-6 border-2 border-indigo-200 hover:border-indigo-300 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900">Roles</h5>
                      <p className="text-sm text-gray-500">Permission sets</p>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-indigo-600">
                    {log.resources_synced?.roles?.total || 0}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Created</span>
                    <span className="font-semibold text-green-600">
                      +{log.resources_synced?.roles?.created || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Updated</span>
                    <span className="font-semibold text-blue-600">
                      {log.resources_synced?.roles?.updated || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Deleted</span>
                    <span className="font-semibold text-orange-600">
                      -{log.resources_synced?.roles?.deleted || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-gray-100">
                    <span className="text-sm text-gray-600">Skipped</span>
                    <span className="font-semibold text-gray-500">
                      {log.resources_synced?.roles?.skipped || 0}
                    </span>
                  </div>
                  {(log.resources_synced?.roles?.failed || 0) > 0 && (
                    <div className="flex items-center justify-between py-2 bg-red-50 px-3 rounded-lg">
                      <span className="text-sm font-medium text-red-600">Failed</span>
                      <span className="font-bold text-red-600">
                        {log.resources_synced.roles.failed}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Performance Metrics */}
        {log.performance && (
          <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-yellow-600" />
              Performance Metrics
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Total Duration</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatDuration(log.performance.total_duration_ms)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Fetch Time</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatDuration(log.performance.fetch_duration_ms || 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Transform Time</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatDuration(log.performance.transform_duration_ms || 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-xs text-gray-600 mb-1">Persist Time</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatDuration(log.performance.persist_duration_ms || 0)}
                </p>
              </div>
            </div>
            <div className="mt-4 bg-white rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Processing Speed</span>
                <span className="text-2xl font-bold text-yellow-600">
                  {log.performance.items_per_second?.toFixed(2)} items/second
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Errors Section */}
        {log.sync_errors && log.sync_errors.length > 0 && (
          <div className="bg-red-50 rounded-xl p-6 border-2 border-red-200">
            <h4 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Errors Encountered ({log.sync_errors.length})
            </h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {log.sync_errors.map((error, index) => (
                <div key={error.id || error._id || index} className="bg-white rounded-lg p-4 border border-red-200">
                  <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono">
                    {JSON.stringify(error, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Conflicts Section */}
        {log.conflicts && log.conflicts.length > 0 && (
          <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-200">
            <h4 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
              Conflicts Detected ({log.conflicts.length})
            </h4>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {log.conflicts.map((conflict, index) => (
                <div key={index} className="bg-white rounded-lg p-4 border border-yellow-200">
                  <pre className="text-sm text-yellow-800 whitespace-pre-wrap font-mono">
                    {JSON.stringify(conflict, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Detailed Changes Section - Organized by Resource Type */}
        {changesByType && (log.detailed_changes?.created_items?.length || 0) + (log.detailed_changes?.updated_items?.length || 0) + (log.detailed_changes?.merged_items?.length || 0) + (log.detailed_changes?.skipped_items?.length || 0) > 0 && (
          <div className="bg-white rounded-xl border-2 border-brand-indigo-200">
            <div className="p-6 border-b border-gray-200">
              <h4 className="text-xl font-bold text-gray-900 mb-4">Detailed Change Log</h4>

              {/* Resource Type Tabs */}
              <div className="flex gap-2 border-b border-gray-200">
                <button
                  onClick={() => setActiveDetailTab('users')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeDetailTab === 'users'
                      ? 'border-b-2 border-brand-blue-600 text-brand-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Users ({changesByType.users.created.length + changesByType.users.updated.length + changesByType.users.merged.length + changesByType.users.skipped.length})
                  </div>
                </button>
                <button
                  onClick={() => setActiveDetailTab('groups')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeDetailTab === 'groups'
                      ? 'border-b-2 border-brand-purple-600 text-brand-purple-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Groups ({changesByType.groups.created.length + changesByType.groups.updated.length + changesByType.groups.merged.length + changesByType.groups.skipped.length})
                  </div>
                </button>
                <button
                  onClick={() => setActiveDetailTab('roles')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeDetailTab === 'roles'
                      ? 'border-b-2 border-brand-indigo-600 text-brand-indigo-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Roles ({changesByType.roles.created.length + changesByType.roles.updated.length + changesByType.roles.merged.length + changesByType.roles.skipped.length})
                  </div>
                </button>
                <button
                  onClick={() => setActiveDetailTab('mappings')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeDetailTab === 'mappings'
                      ? 'border-b-2 border-brand-pink-600 text-brand-pink-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4" />
                    Mappings ({changesByType.mappings.created.length + changesByType.mappings.updated.length + changesByType.mappings.merged.length + changesByType.mappings.skipped.length})
                  </div>
                </button>
              </div>

              {/* Search Input for Active Tab */}
              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={`Search ${activeDetailTab}...`}
                    value={searchQueries[activeDetailTab]}
                    onChange={(e) => setSearchQueries(prev => ({
                      ...prev,
                      [activeDetailTab]: e.target.value
                    }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent text-sm"
                  />
                  {searchQueries[activeDetailTab] && (
                    <button
                      onClick={() => setSearchQueries(prev => ({
                        ...prev,
                        [activeDetailTab]: ''
                      }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6 space-y-4">
              {/* Created Section for Active Tab */}
              {filteredItems && filteredItems.created.length > 0 && (
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                <button
                  onClick={() => toggleSection('created')}
                  className="w-full p-6 flex items-center justify-between hover:bg-green-100/50 transition-colors rounded-xl"
                >
                  <h4 className="text-lg font-semibold text-green-900 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-green-600" />
                    Created ({filteredItems.created.length})
                  </h4>
                  {expandedSections.created ? (
                    <ChevronDown className="w-5 h-5 text-green-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-green-600" />
                  )}
                </button>
                {expandedSections.created && (
                  <div className="px-6 pb-6 space-y-3 max-h-96 overflow-y-auto">
                    {filteredItems.created.map((item: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
                        <div className="flex items-start gap-3 mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{item.resource_name}</p>
                            <p className="text-xs text-gray-500">ID: {item.resource_id}</p>
                          </div>
                        </div>
                        {item.data && (
                          <pre className="text-xs text-gray-700 bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                            {JSON.stringify(item.data, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

              {/* Updated Section for Active Tab */}
              {filteredItems && filteredItems.updated.length > 0 && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                <button
                  onClick={() => toggleSection('updated')}
                  className="w-full p-6 flex items-center justify-between hover:bg-blue-100/50 transition-colors rounded-xl"
                >
                  <h4 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
                    <Edit3 className="w-5 h-5 text-blue-600" />
                    Updated ({filteredItems.updated.length})
                  </h4>
                  {expandedSections.updated ? (
                    <ChevronDown className="w-5 h-5 text-blue-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-blue-600" />
                  )}
                </button>
                {expandedSections.updated && (
                  <div className="px-6 pb-6 space-y-3 max-h-96 overflow-y-auto">
                    {filteredItems.updated.map((item: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{item.resource_name}</p>
                            <p className="text-xs text-gray-500">ID: {item.resource_id}</p>
                          </div>
                        </div>
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Changed Fields:</p>
                          <div className="flex flex-wrap gap-1">
                            {item.changed_fields.map((field, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                        {/* Detailed Changes Diff Viewer */}
                        {item.detailed_changes && item.detailed_changes.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-gray-600 mb-2">Changes:</p>
                            <div className="space-y-1.5">
                              {item.detailed_changes.map((change: any, idx: number) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-lg border-l-4 ${
                                    change.type === 'added' ? 'bg-green-50 border-green-500' :
                                    change.type === 'removed' ? 'bg-red-50 border-red-500' :
                                    'bg-yellow-50 border-yellow-500'
                                  }`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <p className="text-xs font-semibold text-gray-800 mb-1">{change.field}</p>
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <span className="text-gray-500">Old: </span>
                                          <span className={`font-mono ${change.type === 'removed' ? 'text-red-700 line-through' : 'text-gray-700'}`}>
                                            {change.old_value === null ? '(empty)' : typeof change.old_value === 'object' ? JSON.stringify(change.old_value) : change.old_value}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">New: </span>
                                          <span className={`font-mono ${change.type === 'added' ? 'text-green-700 font-semibold' : 'text-gray-700'}`}>
                                            {change.new_value === null ? '(empty)' : typeof change.new_value === 'object' ? JSON.stringify(change.new_value) : change.new_value}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      change.type === 'added' ? 'bg-green-200 text-green-800' :
                                      change.type === 'removed' ? 'bg-red-200 text-red-800' :
                                      'bg-yellow-200 text-yellow-800'
                                    }`}>
                                      {change.type}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          // Fallback: Show old JSON format if no detailed_changes
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-1">Before:</p>
                              <pre className="text-xs text-gray-700 bg-red-50 p-2 rounded border border-red-200 overflow-x-auto">
                                {JSON.stringify(item.old_data, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-gray-600 mb-1">After:</p>
                              <pre className="text-xs text-gray-700 bg-green-50 p-2 rounded border border-green-200 overflow-x-auto">
                                {JSON.stringify(item.new_data, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

              {/* Merged Section for Active Tab */}
              {filteredItems && filteredItems.merged.length > 0 && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
                <button
                  onClick={() => toggleSection('merged')}
                  className="w-full p-6 flex items-center justify-between hover:bg-purple-100/50 transition-colors rounded-xl"
                >
                  <h4 className="text-lg font-semibold text-purple-900 flex items-center gap-2">
                    <GitMerge className="w-5 h-5 text-purple-600" />
                    Merged ({filteredItems.merged.length})
                  </h4>
                  {expandedSections.merged ? (
                    <ChevronDown className="w-5 h-5 text-purple-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-purple-600" />
                  )}
                </button>
                {expandedSections.merged && (
                  <div className="px-6 pb-6 space-y-3 max-h-96 overflow-y-auto">
                    {filteredItems.merged.map((item: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-purple-200 shadow-sm">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{item.resource_name}</p>
                            <p className="text-xs text-gray-500">ID: {item.resource_id}</p>
                          </div>
                        </div>
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-purple-700 mb-1">Merged Fields:</p>
                          <div className="flex flex-wrap gap-1">
                            {item.merged_fields.map((field, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                        {item.data && (
                          <div>
                            <p className="text-xs font-semibold text-gray-600 mb-1">Final Data:</p>
                            <pre className="text-xs text-gray-700 bg-gray-50 p-3 rounded border border-gray-200 overflow-x-auto">
                              {JSON.stringify(item.data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

              {/* Skipped Section for Active Tab */}
              {filteredItems && filteredItems.skipped.length > 0 && (
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border-2 border-gray-300">
                <button
                  onClick={() => toggleSection('skipped')}
                  className="w-full p-6 flex items-center justify-between hover:bg-gray-100/50 transition-colors rounded-xl"
                >
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Ban className="w-5 h-5 text-gray-600" />
                    Skipped ({filteredItems.skipped.length})
                  </h4>
                  {expandedSections.skipped ? (
                    <ChevronDown className="w-5 h-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  )}
                </button>
                {expandedSections.skipped && (
                  <div className="px-6 pb-6 space-y-2 max-h-96 overflow-y-auto">
                    {filteredItems.skipped.map((item: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{item.resource_name}</p>
                              <p className="text-xs text-gray-500">{item.resource_id}</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 italic">{item.reason}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Empty State */}
              {filteredItems &&
               filteredItems.created.length === 0 &&
               filteredItems.updated.length === 0 &&
               filteredItems.merged.length === 0 &&
               filteredItems.skipped.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  {searchQueries[activeDetailTab] ? (
                    <div>
                      <p className="font-medium">No results found for "{searchQueries[activeDetailTab]}"</p>
                      <p className="text-sm mt-2">Try a different search term or clear the search</p>
                    </div>
                  ) : (
                    <p>No changes for {activeDetailTab}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {log.notes && (
          <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h4 className="text-lg font-semibold text-blue-900 mb-2">Notes</h4>
            <p className="text-gray-700">{log.notes}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
